import { FC, useState } from 'react';
import { Dialog } from './Dialogs';
import ItemText from '../widgets/ItemText';
import { TextItem, useZakeke } from '@zakeke/zakeke-configurator-react';
import { T } from '../../Helpers';

import type { PropChangeHandler } from '../layout/Designer';

interface EditTextItem {
	guid: string;
	name: string;
	text: string;
	fillColor: string;
	fontFamily: string;
	fontWeight: string;
	fontSize: number;
	isTextOnPath: boolean;
	constraints: { [key: string]: any } | null;
}

const AddTextDialog: FC<{ onClose: () => void; onConfirm: (item: EditTextItem) => void }> = ({
	onClose,
	onConfirm
}) => {
	const { fonts, getPrintingMethodsRestrictions, defaultFontID, getFontWeights, checkFontSupports } = useZakeke();

	const textRestrictions = getPrintingMethodsRestrictions();
	
	// Trova il font di default basandosi su defaultFontID, altrimenti usa il primo font disponibile
	const getDefaultFont = () => {
		console.log('Default Font ID:', defaultFontID);
		if (defaultFontID !== null && defaultFontID !== undefined) {
			console.log("fonts:", fonts);
			const defaultFont = fonts.find((font) => (font as any).fontID === defaultFontID);
			if (defaultFont) {
				return defaultFont.name;
			}
		}
		return fonts[0].name;
	};




	// Semplificato - environment gestisce il fontWeight automaticamente
	const getInitialFontWeight = () => {
		// Usa un peso di default semplice, environment lo aggiusterà automaticamente
		return '400';
	};
	
	const [item, setItem] = useState<EditTextItem>({
		guid: '',
		name: '',
		text: T._('Text', 'Composer'),
		fillColor: textRestrictions.defaultColor,
		fontFamily: getDefaultFont(),
		fontSize: 48,
		fontWeight: getInitialFontWeight(),
		isTextOnPath: false,
		constraints: null
	});

	const handleItemPropChange: PropChangeHandler = (item, prop, value) => {
		let newItem = { ...item } as EditTextItem;

		switch (prop) {
			case 'text':
				newItem.text = value as string;
				break;
			case 'font-italic':
				// Environment gestirà automaticamente il fontWeight corretto
				// Qui manteniamo solo lo stato locale per la UI
				const weightData = newItem.fontWeight.split(' ');
				const currentIsBold = weightData.length > 1 && (weightData[1] === 'bold' || parseInt(weightData[1]) >= 700);
				
				if (value) {
					newItem.fontWeight = currentIsBold ? 'italic 700' : 'italic 400';
				} else {
					newItem.fontWeight = currentIsBold ? '700' : '400';
				}
				break;
			case 'font-bold':
				// Environment gestirà automaticamente il fontWeight corretto
				// Qui manteniamo solo lo stato locale per la UI
				const currentWeightData = newItem.fontWeight.split(' ');
				const currentIsItalic = currentWeightData.length > 1 && currentWeightData[0] === 'italic';
				
				if (value) {
					newItem.fontWeight = currentIsItalic ? 'italic 700' : '700';
				} else {
					newItem.fontWeight = currentIsItalic ? 'italic 400' : '400';
				}
				break;
			case 'font-color':
				newItem.fillColor = value as string;
				break;
			case 'font-family':
				newItem.fontFamily = value as string;
				
				// Controlla compatibilità e mantieni stili se supportati dal nuovo font
				const selectedFont = fonts.find(font => font.name === value as string);
				if (selectedFont) {
					const currentWeightData = newItem.fontWeight.split(' ');
					const currentIsBold = currentWeightData.length > 1 && (currentWeightData[1] === 'bold' || parseInt(currentWeightData[1]) >= 700);
					const currentIsItalic = currentWeightData.length > 1 && currentWeightData[0] === 'italic';
					
					// Verifica se il nuovo font supporta gli stili correnti
					const fontSupports = checkFontSupports(selectedFont.name, currentIsBold, currentIsItalic);
					
					// Mantieni gli stili se supportati, altrimenti resetta a normal
					if (!fontSupports.canKeepStyles) {
						newItem.fontWeight = '400'; // Reset a default se stili non supportati
					}
					
					console.log('[FONT-WEIGHT-DEBUG] Font changed to:', value, 'preserved styles:', fontSupports.canKeepStyles, 'new fontWeight:', newItem.fontWeight);
				}
				break;
			case 'text-path':
				newItem.isTextOnPath = value as boolean;
				break;
		}

		setItem(newItem);
	};

	return (
		<Dialog
			title={T._('Add text', 'Composer')}
			buttons={[{ label: T._('Confirm', 'Composer'), onClick: () => onConfirm(item) }]}
		>
			<ItemText
				key={item.guid}
				item={item as TextItem}
				handleItemPropChange={handleItemPropChange}
				hideRemoveButton={true}
			/>
		</Dialog>
	);
};

export default AddTextDialog;